import { useLocale } from "../hooks/useLocale";
import { msg } from "../locales/translate";
import { type ReactNode } from "react";
import { cssTransition, toast, ToastContainer, type ToastContent, type ToastOptions } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

type TNotificationContent = ReactNode | (() => ReactNode);
type TNotificationOptions = Pick<ToastOptions, "autoClose" | "toastId">;

const notificationTransition = cssTransition({
	enter: "crm-toast-enter",
	exit: "crm-toast-exit",
	collapse: true,
	collapseDuration: 120,
});

const defaultOptions: ToastOptions = {
	position: "top-center",
	hideProgressBar: true,
	closeOnClick: false,
	pauseOnHover: true,
	pauseOnFocusLoss: false,
	draggable: false,
	theme: "light",
	transition: notificationTransition,
};

const notificationStyles = `
.crm-toast-enter {
	animation: crmToastIn 160ms cubic-bezier(.2, .8, .2, 1) both;
}

.crm-toast-exit {
	animation: crmToastOut 120ms ease-in both;
}

.Toastify__toast.crm-toast {
	border-radius: 10px;
	box-shadow: 0 12px 34px rgba(15, 23, 42, .14), 0 2px 10px rgba(15, 23, 42, .08);
	font-family: inherit;
	font-size: 13px;
	line-height: 18px;
	min-height: 44px;
	padding: 10px 12px;
}

.Toastify__toast.crm-toast .Toastify__toast-body {
	margin: 0;
	padding: 0;
}

@keyframes crmToastIn {
	from { opacity: 0; transform: translate3d(0, -8px, 0) scale(.98); }
	to { opacity: 1; transform: translate3d(0, 0, 0) scale(1); }
}

@keyframes crmToastOut {
	from { opacity: 1; transform: translate3d(0, 0, 0) scale(1); }
	to { opacity: 0; transform: translate3d(0, -6px, 0) scale(.98); }
}
`;

const showNotification = (type: "info" | "success" | "error", content: TNotificationContent, options?: TNotificationOptions) => {
	return toast[type](content as ToastContent, {
		...defaultOptions,
		autoClose: type === "error" ? 5000 : 2200,
		...options,
	});
};

export const getNotificationErrorMessage = (error: unknown, fallback = msg("somethingWentWrong")) => {
	if (error && typeof error === "object" && "message" in error && typeof error.message === "string") return error.message;
	return fallback;
};

export const notify = {
	info: (content: TNotificationContent, options?: TNotificationOptions) => showNotification("info", content, options),
	success: (content: TNotificationContent, options?: TNotificationOptions) => showNotification("success", content, options),
	error: (content: TNotificationContent, options?: TNotificationOptions) => showNotification("error", content, options),
	copied: () => showNotification("info", "Copied", { autoClose: 1200, toastId: "clipboard-copied" }),
};

export const NotificationContainer = () => { useLocale(); return ((
	<>
		<style>{notificationStyles}</style>
		<ToastContainer {...defaultOptions} autoClose={2200} limit={4} newestOnTop toastClassName="crm-toast" />
	</>
)); };